import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Avatar,
  Slide
} from '@material-ui/core';
import { WarningAmber, AccessTime, Autorenew, ErrorOutline } from '@material-ui/icons';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
import { REST_API_END_POINT } from 'src/constants/DefaultValues';

export default function GlobalOvertimeMonitor() {
  const [open, setOpen] = useState(false);
  const [overtimeTicket, setOvertimeTicket] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let isMounted = true;
    let timer = null;

    const checkOvertime = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;
        const user = JSON.parse(userStr);
        if (!user || !user.user_id) return;

        const response = await axios.post(`${REST_API_END_POINT}tickets/check-active-overtime`, {
          userId: user.user_id
        });

        if (response.data && response.data.status === 1 && isMounted) {
          const tickets = response.data.overtimeTickets || [];
          if (tickets.length > 0) {
            // Check session storage to see if we already acknowledged this ticket
            let ignoredTickets = [];
            try {
              ignoredTickets = JSON.parse(sessionStorage.getItem('ignored_overtime_tickets') || '[]');
            } catch (e) {}

            const unacknowledged = tickets.find(t => !ignoredTickets.includes(t.taskId));
            
            if (unacknowledged && !open) {
              setOvertimeTicket({ ...unacknowledged, userId: user.user_id });
              setOpen(true);
            }
          }
        }
      } catch (error) {
        console.error("Error checking overtime:", error);
      }
    };

    // Check immediately on mount
    checkOvertime();
    // Then poll every 30 seconds
    timer = setInterval(checkOvertime, 30000);

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [open]);

  const handleContinue = () => {
    if (overtimeTicket) {
      let ignoredTickets = [];
      try {
        ignoredTickets = JSON.parse(sessionStorage.getItem('ignored_overtime_tickets') || '[]');
      } catch (e) {}
      
      ignoredTickets.push(overtimeTicket.taskId);
      sessionStorage.setItem('ignored_overtime_tickets', JSON.stringify(ignoredTickets));
    }
    setOpen(false);
    setOvertimeTicket(null);
  };

  const handleClockOut = async () => {
    if (!overtimeTicket) return;
    try {
      const now = Math.round(moment().valueOf() / 1000);
      await axios.post(`${REST_API_END_POINT}tickets/update-staff-ticket-timelog`, {
        taskId: overtimeTicket.taskId,
        timeLogId: overtimeTicket.timeLogId,
        startTime: overtimeTicket.startTime,
        now,
        teamId: overtimeTicket.userId
      });

      enqueueSnackbar('You have been successfully clocked out.', { variant: 'success' });
      
      // Also ignore it so it doesn't pop up again before the list refreshes
      let ignoredTickets = [];
      try {
        ignoredTickets = JSON.parse(sessionStorage.getItem('ignored_overtime_tickets') || '[]');
      } catch (e) {}
      ignoredTickets.push(overtimeTicket.taskId);
      sessionStorage.setItem('ignored_overtime_tickets', JSON.stringify(ignoredTickets));

      // Attempt to refresh the page or time logs if they are on ViewTickets
      // We can trigger a custom event that ViewTickets can optionally listen to
      window.dispatchEvent(new Event('timelog_updated'));
      
    } catch (error) {
      enqueueSnackbar('Error clocking out. Please try again.', { variant: 'error' });
      console.error(error);
    }
    setOpen(false);
    setOvertimeTicket(null);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 24,
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          overflow: 'hidden'
        }
      }}
      sx={{ zIndex: 9999 }}
    >
      <DialogTitle sx={{ 
        p: 0, 
        background: 'linear-gradient(135deg, #ff4e50 0%, #f9d423 100%)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 4,
        pb: 3
      }}>
        <Avatar 
          sx={{ 
            width: 70, 
            height: 70, 
            bgcolor: 'white', 
            color: 'error.main',
            boxShadow: '0 8px 24px rgba(255,78,80,0.3)',
            mb: 2
          }}
        >
          <WarningAmber sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
          Overtime Alert
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 3, pb: 2 }}>
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            You have exceeded the expected hours for the following ticket:
          </Typography>

          {overtimeTicket && (
            <Paper 
              elevation={0}
              sx={{ 
                width: '100%', 
                p: 3, 
                borderRadius: 4, 
                bgcolor: 'rgba(255, 78, 80, 0.04)',
                border: '1px solid rgba(255, 78, 80, 0.1)',
                mb: 3
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#222' }}>
                {overtimeTicket.taskName}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <AccessTime color="action" sx={{ mb: 1, opacity: 0.7 }} />
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Expected
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#555' }}>
                      {overtimeTicket.expectedHours}h
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Autorenew color="primary" sx={{ mb: 1, opacity: 0.8 }} />
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Logged
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {overtimeTicket.totalHours}h
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <ErrorOutline color="error" sx={{ mb: 1 }} />
                    <Typography variant="caption" color="error" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Overtime
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main' }}>
                      +{overtimeTicket.overtimeAmount}h
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
          
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Do you wish to continue working on this ticket or clock out now?
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center', gap: 2 }}>
        <Button 
          variant="outlined" 
          color="error" 
          onClick={handleClockOut}
          size="large"
          sx={{ 
            borderRadius: 8, 
            px: 4, 
            py: 1.2,
            borderWidth: 2,
            fontWeight: 700,
            '&:hover': {
              borderWidth: 2
            }
          }}
        >
          Stop & Clock Out
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleContinue}
          size="large"
          sx={{ 
            borderRadius: 8, 
            px: 4, 
            py: 1.2,
            fontWeight: 700,
            boxShadow: '0 8px 16px rgba(0, 122, 255, 0.25)',
            '&:hover': {
              boxShadow: '0 12px 20px rgba(0, 122, 255, 0.4)'
            }
          }}
        >
          Continue Working
        </Button>
      </DialogActions>
    </Dialog>
  );
}
